/**
 * A situação do contrato de uma obra, para o acompanhamento de contratos.
 *
 * Mora no pacote compartilhado pela mesma razão do prazo e das pendências: o
 * servidor apura e a tela mostra, e duas contas da mesma regra chegam a dois
 * números diferentes. O "vencido" daqui é o mesmo `contrato_vencido` do painel —
 * fim da vigência antes de hoje —, para os dois nunca discordarem.
 */

import { obraEncerrada, type ObraStatus } from './domain.js';
import { daysBetween } from './format.js';

/**
 * Com quantos dias de antecedência o contrato passa a "a vencer".
 *
 * Sessenta, e não trinta: o que se faz com um contrato perto do fim é o termo
 * aditivo de prazo — justificativa técnica, parecer jurídico, assinatura —, e
 * esse caminho leva semanas. Avisar com trinta dias deixa pouco tempo para
 * percorrê-lo antes de a vigência acabar.
 */
export const CONTRATO_A_VENCER_DIAS = 60;

/** Na ordem de urgência: é a ordem das abas e da planilha. */
export const SITUACOES_DE_CONTRATO = [
  'vencido',
  'a_vencer',
  'vigente',
  'sem_vigencia',
  'encerrado',
] as const;
export type SituacaoDeContrato = (typeof SITUACOES_DE_CONTRATO)[number];

export const SITUACAO_DE_CONTRATO_LABELS: Record<SituacaoDeContrato, string> = {
  vencido: 'Vencido',
  a_vencer: 'A vencer',
  vigente: 'Vigente',
  sem_vigencia: 'Sem vigência',
  encerrado: 'Encerrado',
};

export interface SituacaoApurada {
  readonly situacao: SituacaoDeContrato;
  /**
   * Dias de hoje até o fim da vigência: negativo depois de vencido, zero no
   * último dia. Nulo sem data de fim, e na obra encerrada, em que a contagem
   * deixou de importar.
   */
  readonly diasRestantes: number | null;
}

/**
 * A situação do contrato hoje.
 *
 * **Obra encerrada encerra o acompanhamento**: concluída ou cancelada, o
 * contrato não tem mais prazo a cuidar, e mostrá-lo como "vencido" em vermelho
 * seria cobrar providência de algo que já terminou — o mesmo motivo de o painel
 * não dar pendência a obra encerrada. A paralisada continua sendo acompanhada:
 * a vigência corre (ou é prorrogada por aditivo), e é justamente nela que um
 * vencimento passa despercebido.
 *
 * `hoje` é o hoje do órgão, no fuso dele, em `yyyy-mm-dd`.
 */
export function situacaoDoContrato(
  obra: { readonly status: ObraStatus; readonly contractEnd: string | null },
  hoje: string,
): SituacaoApurada {
  if (obraEncerrada(obra.status)) return { situacao: 'encerrado', diasRestantes: null };
  if (!obra.contractEnd) return { situacao: 'sem_vigencia', diasRestantes: null };

  const diasRestantes = daysBetween(hoje, obra.contractEnd);
  if (obra.contractEnd < hoje) return { situacao: 'vencido', diasRestantes };
  if (diasRestantes <= CONTRATO_A_VENCER_DIAS) return { situacao: 'a_vencer', diasRestantes };
  return { situacao: 'vigente', diasRestantes };
}
